"use client";

import { applyEnvPrefix, type DomainMarket } from "@/lib/domain/host-map";

interface LocationLike {
  host: string;
  pathname: string;
  search: string;
}

/**
 * URL of the same page on another market's domain, staying within the current
 * environment (e.g. stg.tongkatfitness.com → stg.tongkatfitness.uk). Null when
 * already on that domain.
 */
export function buildRegionHref(
  entry: DomainMarket,
  location: LocationLike,
): string | null {
  const targetHost = applyEnvPrefix(entry.host, location.host);
  if (targetHost === location.host.toLowerCase().split(":")[0]) return null;
  return `https://${targetHost}${location.pathname}${location.search}`;
}

interface UseCountrySwitchResult {
  /** Link to the current page on `entry`'s domain, or null (same domain / SSR). */
  regionHref: (entry: DomainMarket) => string | null;
}

/**
 * Each market lives on its own public domain, so switching region is a
 * cross-domain navigation (e.g. tongkatfitness.us → tongkatfitness.uk),
 * preserving the current clean path and staying within the same environment.
 *
 * Render the result as a real `<a href>`: Google's cross-domain linker only
 * decorates link clicks (adds `_gl`), so a scripted navigation would start a new
 * session and drop the visitor's source and Google Ads click ID.
 *
 * The cart cookie is host-scoped and does not follow across domains — the
 * destination domain starts a fresh cart, which is the intended behaviour for
 * separate-currency markets.
 */
export function useCountrySwitch(): UseCountrySwitchResult {
  const regionHref = (entry: DomainMarket): string | null =>
    typeof window === "undefined"
      ? null
      : buildRegionHref(entry, window.location);
  return { regionHref };
}
